import { auth } from '@axo/auth';
import { Brand } from '@axo/config/@types/Brand.types';
import { tracking } from '@axo/form/data-access/tracking';
import {
  Product,
  customer,
  loan_application,
  person,
} from '@axo/shared/data-access/types';
import { MarketCountryCode } from '@axo/shared/types';
import { useSubject } from '@axo/shared/util/reactivity';
import { loanApplicationStore } from '@axo/store';
import { captureException } from '@sentry/react';
import { useEffect, useMemo, useState } from 'react';
import { apiOrchestratorFactory, queueOptions } from './api-orchestrator';
import {
  amountOfChildrenHandler,
  carHandler,
  childHandler,
  coCustomerHandler,
  coPersonHandler,
  customerHandler,
  debtHandler,
  expenseHandler,
  leadHandler,
  loanApplicationHandler,
  marketingConsentHandler,
  personHandler,
} from './handlers';
import { AmountOfChildren } from './handlers/amountOfChildren';
import { reduceChangesToObjects } from './reduceChangesToObjects';
import { Tokens, reloadState } from './reloadState';
import { reloadData } from './sessionStorage';
import { IPreconditions } from './types/IPreconditions';
import { ReloadedFormData } from './types/dataTypes';

const onPreconditionChange = (
  preconditions: IPreconditions,
  product?: Product
) => {
  const { customerJWT, loanApplicationId, broker } = preconditions;

  if (!customerJWT || !loanApplicationId) return;

  const { customerJWT: oldCustomerJWT, applicationId: oldLoanApplicationId } =
    reloadData.get(product);

  if (
    oldCustomerJWT === customerJWT &&
    oldLoanApplicationId === loanApplicationId
  )
    return;

  try {
    auth().setToken(customerJWT);
    auth().setCustomerId(preconditions.customerId || null);
    loanApplicationStore().setActiveApplicationId(loanApplicationId);
  } catch (e) {
    captureException(e);
  }

  reloadData.set({
    product,
    applicationId: loanApplicationId,
    customerJWT,
    broker,
  });
};

export type Mutators<T> = {
  toApi: (data: T) => T & Record<string, unknown>;
  fromApi: (data: ReloadedFormData) => T;
  toApiLastSync?: (data: T) => T & Record<string, unknown>;
};

interface IUseBackendSyncProps<T> {
  marketCountry: MarketCountryCode;
  // TODO: More explicit return types that combines API and form types
  mutators: Mutators<T>;
  baseUrl: string;
  product?: Product;
  brand?: Brand;
  initialSnapshotStepName: string;
}

export type IUseBackendSync<T extends object> = ReturnType<
  typeof useBackendSync<T>
>;

type IData = {
  LoanApplication: loan_application.LoanApplication;
  Customer: customer.CreateCustomer;
  // marketing consent is a part of customer, but we handle it separately
  MarketingConsent: customer.CreateCustomer;
  CoCustomer: customer.CreateCustomer;
  Person: person.Person;
  CoPerson: person.Person;
  Debt: loan_application.Debt;
  CoDebt: loan_application.Debt;
  AmountOfChildren: AmountOfChildren;
  Car: loan_application.Car;
  Child: loan_application.Child;
  Expense: loan_application.Expense;
  Lead: loan_application.CreateLead;
};

export const useBackendSync = <T extends object>({
  marketCountry,
  mutators,
  baseUrl,
  product = Product.UnsecuredLoan,
  brand,
  initialSnapshotStepName,
}: IUseBackendSyncProps<T>) => {
  const [initialized, setInitialized] = useState(false);
  const [reloadedData, setReloadedData] = useState<T>();

  const apiOrchestrator = useMemo(
    () =>
      apiOrchestratorFactory<IData, IPreconditions>({
        product,
        handlers: {
          LoanApplication: loanApplicationHandler,
          Customer: customerHandler,
          CoCustomer: coCustomerHandler,
          MarketingConsent: marketingConsentHandler,
          Person: personHandler,
          CoPerson: coPersonHandler,
          Debt: debtHandler,
          CoDebt: debtHandler,
          AmountOfChildren: amountOfChildrenHandler,
          Car: carHandler,
          Child: childHandler,
          Expense: expenseHandler,
          Lead: leadHandler,
        },
        baseUrl,
        initialPreconditions: { brand, ids: {}, initialSnapshotStepName },
        onPreconditionChange,
      }),
    [product]
  );

  const state = useSubject(apiOrchestrator.state);

  const fetchInitialData = async (tokens: Tokens) => {
    const { data, loanApplicationId, JWT, preconditions, broker } =
      await reloadState({
        marketCountry,
        baseUrl,
        tokens,
      });
    apiOrchestrator.setPreconditions(preconditions);

    setReloadedData(mutators.fromApi(data));

    try {
      auth().setToken(JWT);
      auth().setCustomerId(preconditions.customerId || null);
      loanApplicationStore().setActiveApplicationId(loanApplicationId);
    } catch (e) {
      captureException(e);
    }

    reloadData.set({
      product,
      applicationId: loanApplicationId,
      customerJWT: JWT,
      broker,
    });
    await tracking.initialize({
      loanApplicationId,
      token: JWT,
      baseUrl,
    });
    setInitialized(true);
  };

  const awaitLoadingComplete = () => {
    return new Promise<void>((resolve) => {
      if (!apiOrchestrator.state.value.isLoading) resolve();

      const subscriber = ({ isLoading }: { isLoading: boolean }) => {
        if (!isLoading) {
          apiOrchestrator.state.unsubscribe(subscriber);
          resolve();
        }
      };

      apiOrchestrator.state.subscribe(subscriber);
    });
  };

  const queue = (data: T, options?: queueOptions) => {
    const mutated = mutators.toApi(data);
    const reduced = reduceChangesToObjects(mutated);

    return apiOrchestrator.queue(reduced, options);
  };

  const queueImmediate = async (data: T) => {
    await queue(data, { immediate: true });
    return awaitLoadingComplete();
  };

  const reload = async () => {
    const { applicationId, customerJWT } = reloadData.get(product);
    if (!applicationId || !customerJWT) {
      throw new Error(
        'Can\t reload data, missing applicationId or customerJWT'
      );
    }

    fetchInitialData({ applicationId, customerJWT });
  };

  useEffect(() => {
    const params = new URL(window.location.href).searchParams;
    const magicToken = params.get('token');

    const { applicationId, customerJWT } = reloadData.get(product);

    if (!(applicationId && customerJWT) && !magicToken) {
      return setInitialized(true);
    }

    const tokens: Tokens = { applicationId, customerJWT, magicToken };

    fetchInitialData(tokens).catch((e) => {
      captureException(e);
      reloadData.reset();
      setInitialized(true);
    });
  }, []);

  return {
    ...state,
    reloadedData,
    initialized,
    queue,
    queueImmediate,
    reload,
    getPreconditions: apiOrchestrator.getPreconditions,
  };
};
